/**
 * Visor e discagem dos modos inline/min e flutuante (FloatingDialer).
 *
 * Separa o que o usuário DIGITOU (mostrado no visor, nunca alterado) do que
 * será DISCADO (normalizado só quando o número está completo). O DDD padrão
 * e o nono dígito de celular antigo são aplicados ao colar e ao discar, nunca
 * "inventados" no visor enquanto se digita.
 */
import { isValidE164PhoneNumber, matchNonGeographicNumber, normalizeBrazilianPhone, onlyDigits } from './phoneUtils';

export const DEFAULT_COUNTRY_PREFIX = '+55';
export const MAX_DIAL_LENGTH = 32;

/** Mantém só dígitos, * e #; o + é aceito apenas como primeiro caractere. */
export function sanitizeDialInput(value: string): string {
  if (!value) {
    return '';
  }

  const normalized = value.normalize('NFKC');
  let result = '';

  for (const char of normalized) {
    if (/^[0-9*#]$/.test(char)) {
      result += char;
      continue;
    }

    if (char === '+' && result.length === 0) {
      result += char;
    }
  }

  return result;
}

/**
 * Formata o que foi digitado para exibição: "+55 (51) 3248-8509".
 * Nunca acrescenta nem remove dígitos.
 */
export function formatDialerDisplay(rawValue: string, defaultPrefix: string = DEFAULT_COUNTRY_PREFIX): string {
  const sanitized = sanitizeDialInput(rawValue);
  if (!sanitized) {
    return '';
  }

  const hasExplicitPrefix = sanitized.startsWith('+');
  const numeric = sanitized.replace(/[^0-9]/g, '');
  const defaultDigits = defaultPrefix.replace(/[^0-9]/g, '');
  const expectedCountryLength = defaultDigits.length || 2;
  const derivedCountry = hasExplicitPrefix
    ? numeric.slice(0, Math.min(expectedCountryLength, numeric.length))
    : defaultDigits || numeric.slice(0, expectedCountryLength);

  let localDigits = hasExplicitPrefix ? numeric.slice(derivedCountry.length) : numeric;
  const countryPrefix = hasExplicitPrefix ? `+${derivedCountry}` : defaultPrefix;

  // 0800 123 4567 (não geográfico); o 0 só aparece se foi digitado
  const nonGeo = derivedCountry === '55' ? localDigits.match(/^(0?)((?:800|300|500|900))(\d{0,7})$/) : null;
  if (nonGeo) {
    const rest = nonGeo[3];
    const tail = rest.length <= 3 ? rest : `${rest.slice(0, 3)} ${rest.slice(3)}`;
    return `${countryPrefix} ${nonGeo[1]}${nonGeo[2]}${tail ? ` ${tail}` : ''}`;
  }

  if (derivedCountry === '55' && localDigits.length >= 8) {
    const area = localDigits.slice(0, 2);
    const core = localDigits.slice(2, Math.max(localDigits.length - 4, 2));
    const tail = localDigits.slice(-4);
    return `${countryPrefix} (${area}) ${core}-${tail}`.replace(/\s+/g, ' ').trim();
  }

  if (!localDigits) {
    return countryPrefix.trim();
  }

  const groups: string[] = [];
  while (localDigits.length > 4) {
    groups.push(localDigits.slice(0, 3));
    localDigits = localDigits.slice(3);
  }
  if (localDigits) {
    groups.push(localDigits);
  }

  const formatted = groups.join(' ');
  return `${countryPrefix} ${formatted}`.replace(/\s+/g, ' ').trim();
}

/**
 * Número E.164 a discar a partir do que foi DIGITADO.
 * - "+..." internacional: usado como está.
 * - Menos de 10 dígitos: null (incompleto; o DDD padrão só vale ao colar).
 * - 10+ dígitos: remove 0/55/operadora e acrescenta o 9 apenas em celular antigo
 *   não ambíguo (6, 7, 8). Um local começando com 9 pode ser um celular de 9
 *   dígitos ainda incompleto, então não ganha o 9 enquanto se digita.
 */
export function normalizeTypedToE164(rawValue: string, defaultPrefix: string = DEFAULT_COUNTRY_PREFIX): string | null {
  const sanitized = sanitizeDialInput(rawValue);
  if (!sanitized) {
    return null;
  }

  if (sanitized.startsWith('+')) {
    const international = sanitized.replace(/[^0-9]/g, '');
    return international ? `+${international}` : null;
  }

  const digits = onlyDigits(sanitized);
  if (!digits) {
    return null;
  }

  const prefixDigits = defaultPrefix.replace(/[^0-9]/g, '');
  if (prefixDigits !== '55') {
    return prefixDigits ? `+${prefixDigits}${digits}` : `+${digits}`;
  }

  const national = normalizeBrazilianPhone(digits, { defaultDDD: null, legacyMobile: 'unambiguous' });
  if (national.length < 10 && !matchNonGeographicNumber(national)) {
    return null;
  }

  return `+55${national}`;
}

export interface DialPlan {
  /** Valor digitado já sanitizado (o que o visor mostra) */
  raw: string;
  /** Visor: formatação do que foi digitado */
  display: string;
  /** Número que será discado (E.164) ou null se incompleto */
  e164: string | null;
  /** Número que será discado, formatado para exibir */
  dialDisplay: string | null;
  /** Se o número a discar é válido (libphonenumber, metadados completos) */
  isValid: boolean;
  /** Se o número a discar difere do que foi digitado (ex.: 0+operadora removidos, 9 acrescentado) */
  differs: boolean;
}

export function buildDialPlan(rawValue: string, defaultPrefix: string = DEFAULT_COUNTRY_PREFIX): DialPlan {
  const raw = sanitizeDialInput(rawValue).slice(0, MAX_DIAL_LENGTH);
  const display = formatDialerDisplay(raw, defaultPrefix);
  const e164 = normalizeTypedToE164(raw, defaultPrefix);
  const isValid = isValidE164PhoneNumber(e164);
  const dialDisplay = e164 ? formatDialerDisplay(e164, defaultPrefix) : null;
  const typedDigits = raw.startsWith('+') ? onlyDigits(raw) : `${defaultPrefix.replace(/[^0-9]/g, '')}${onlyDigits(raw)}`;
  const differs = Boolean(e164) && onlyDigits(e164 ?? '') !== typedDigits;
  return { raw, display, e164, dialDisplay, isValid, differs };
}
